import { designAPI } from '@api/designs';
import { Box, ClickAwayListener, List, ListItem, Paper, Popper, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ConfirmModal } from './dialogs/ConfirmModal';
import { LoadingScreen } from './LoadingScreen';
import { useAlert } from '@hooks/useAlert';

import type { RootState } from '@features/redux/store';
import type { AutocompleteResponse } from '@api/designs';

export const DeviceInfoAutocomplete = ({
    modelName,
    manufacturer,
    setModelName,
    setTags,
    setCategory,
}: {
    modelName: string;
    manufacturer: string;
    setModelName: (value: string) => void;
    setTags: (value: string[]) => void;
    setCategory: (value: string) => void;
}) => {
    const { IdToken } = useSelector((state: RootState) => state.auth);

    const textRef = useRef<HTMLInputElement>(null);

    const { setAlert } = useAlert();

    const [suggestions, setSuggestions] = useState<AutocompleteResponse[]>([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [deviceLoading, setDeviceLoading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [dataToConfirm, setDataToConfirm] = useState<{ tags: string[]; category: string; categoryId: number } | null>(
        null
    );

    useEffect(() => {
        setSuggestions([]);
    }, [manufacturer]);

    const search = async (searchText: string) => {
        setModelName(searchText);
        // if manufacturer is not selected, don't search, we need manufacturer to get correct suggestions
        if (!manufacturer) return;
        setSuggestionsLoading(true);
        // Add selected manufacturer to search to get correct suggestions
        const devices = await designAPI(IdToken!).deviceInfoAutocomplete(`${manufacturer} ${searchText}`);
        setSuggestionsLoading(false);
        setSuggestions(devices);
    };

    const populateTags = async (suggestion: AutocompleteResponse) => {
        setDeviceLoading(true);
        try {
            // the models is contained within the misc category, we must obtain that to get the model ids
            const device = await designAPI(IdToken!).deviceInfoDetail(suggestion.id);

            setDataToConfirm({ tags: device.tags, category: device.category, categoryId: device.categoryId });

            if (!device.tags.length) {
                setAlert(
                    "No tags found on Techspecs. Please use the manufacturer's website to find & populate model IDs.",
                    'warning'
                );
            }
        } catch (e) {
            setAlert('Error getting tags and category. Contact tech with this issue.', 'error');
        }

        setDeviceLoading(false);
    };

    return (
        <>
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={'20px'}>
                <TextField
                    inputRef={textRef}
                    placeholder='Enter model name (e.g. iPhone 14 Pro)'
                    variant='outlined'
                    value={modelName}
                    label={'Model Name'}
                    onChange={(e) => {
                        search(e.target.value);
                        setMenuOpen(true);
                    }}
                    onClick={() => {
                        setMenuOpen(true);
                        search(modelName);
                    }}
                    sx={{ width: '250px', mt: '20px' }}
                />
            </Box>

            {/* zIndex is set to 1300(dialog has 1300 for z-index) to make sure the popper is on top of the dialog */}
            <Popper
                open={menuOpen}
                anchorEl={textRef.current}
                placement='bottom-start'
                sx={{ width: '250px', zIndex: 1300 }}>
                <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
                    <List component={Paper}>
                        {suggestionsLoading && <ListItem>Loading...</ListItem>}

                        {!manufacturer && <ListItem>Select manufacturer to get suggestions</ListItem>}

                        {suggestions.length === 0 && !suggestionsLoading && manufacturer && (
                            <ListItem>No suggestions</ListItem>
                        )}

                        {!suggestionsLoading &&
                            manufacturer &&
                            suggestions.map((suggestion) => (
                                <ListItem
                                    key={suggestion.id}
                                    button
                                    onClick={() => {
                                        setModelName(suggestion.model);
                                        populateTags(suggestion);
                                        setMenuOpen(false);
                                    }}>
                                    {suggestion.model}
                                </ListItem>
                            ))}
                    </List>
                </ClickAwayListener>
            </Popper>

            <ConfirmModal
                open={Boolean(dataToConfirm)}
                title={'Do you want to update the search tags and fill the category?'}
                setOpen={() => setDataToConfirm(null)}
                onConfirm={() => {
                    setTags(dataToConfirm!.tags);
                    setCategory(dataToConfirm!.categoryId.toString());
                    setDataToConfirm(null);
                }}
                onCancel={() => {
                    setDataToConfirm(null);
                }}>
                <div>
                    <p>Tags: {dataToConfirm?.tags.join(', ')}</p>
                </div>
                <div>
                    <p>Category: {dataToConfirm?.category}</p>
                </div>
            </ConfirmModal>

            <LoadingScreen active={deviceLoading} />
        </>
    );
};
